import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { listExpenses } from '@/features/finance/ledger/api';
import { formatMoney } from '@/lib/money';
import { categoryLabel, frequencyLabel } from '../constants';
import { useRecurringExpenseSummary, useRecurringExpenses } from '../queries';

const usd = (v: string | null | undefined) => formatMoney(v, 'USD') ?? '—';
const fmtDate = (d: string) => new Date(d).toLocaleDateString();

/**
 * HR-profile "Expenses" tab body (ADR-047, finance-gated). Shows the employee's
 * recurring cost templates + their monthly run-rate, plus recently-generated
 * actual ledger rows. Read-only — manage from Finance → Recurring expenses.
 */
export function EmployeeCostPanel({ employeeId }: { employeeId: number }) {
  const { data, isPending } = useRecurringExpenses({ employee: employeeId, is_active: true });
  const { data: summary } = useRecurringExpenseSummary({ employee: employeeId });
  const actuals = useQuery({
    queryKey: ['employee-expenses', employeeId],
    queryFn: () => listExpenses({ employee: employeeId, ordering: '-date' }),
  });

  if (isPending) return <Skeleton className="min-h-48 w-full" />;

  const recurring = data?.results ?? [];
  const generated = actuals.data?.results ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-md border p-3">
        <p className="text-muted-foreground text-xs">Recurring cost / month</p>
        <p className="text-lg font-semibold tabular-nums">{usd(summary?.employee_monthly_base)}</p>
        <p className="text-muted-foreground text-[11px]">Active subscriptions, normalized to a monthly run-rate (USD).</p>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium">Subscriptions</h3>
        {recurring.length === 0 ? (
          <div className="text-muted-foreground flex min-h-24 items-center justify-center py-6 text-center text-sm">
            No recurring expenses for this employee.
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="hidden sm:table-cell">Category</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Monthly (USD)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recurring.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">
                      {r.description}
                      {r.vendor ? <span className="text-muted-foreground"> · {r.vendor}</span> : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell">
                      {categoryLabel(r.category)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(r.amount, r.currency) ?? '—'}
                      <span className="text-muted-foreground"> / {frequencyLabel(r.frequency).toLowerCase()}</span>
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {usd(r.monthly_base_amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {generated.length > 0 ? (
        <div>
          <h3 className="mb-2 text-sm font-medium">Recent charges</h3>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount (USD)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {generated.slice(0, 12).map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-muted-foreground">{fmtDate(e.date)}</TableCell>
                    <TableCell>
                      {e.description}
                      {e.is_recurring ? (
                        <Badge variant="outline" className="ml-2 text-[10px]">Recurring</Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{usd(e.base_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
